import { isEmpty } from 'underscore';
import { useCallback, useMemo, useContext, useState } from 'react';
import { useSelector } from '@xstate/react';
import { isCanceling, StepEvents } from '@bingads-webui-campaign-react/step-wizard-machine';
import { getUrlOptionsDetails } from '@bingads-webui-campaign-react/url-options';
import { getBidBudgetTitleDetails, getConversionGoalTitleDetails, getStoreTitleDetails } from '@bingads-webui-campaign-react/wizard-shared-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';
import { useExitWithoutSavingConfirmation } from '@bingads-webui-campaign-react/step-wizard';
import { useImpulse, usePrevProp, useIsMounted } from '@bingads-webui-campaign-react/step-wizard-controls';
import { needsSaving } from './step-campaign-settings-impl';
import { serverErrorsCompare } from '../shared';

const doesNeedSavingSelector = state => !isCanceling(state) && needsSaving(state.context);
const hideStepHeaderSelector = state => state.context.stepInfo.hideStepHeader;
const scenarioNameSelector = state => state.context.stepsData.scenarioName;
const campaignObjectiveSelector = state => state.context.stepsData.campaignObjective;
const serverContextSelector = state => state.context.stepsData.serverContext;
const campaignIdSelector = state => state.context.stepsData.campaignId;
const campaignSelector = state => state.context.data.campaign;
const locationTargetsSelector = state => state.context.data.campaign.data.locationTargets;
const adSchedulesSelector = state => state.context.data.campaign.data.adSchedules;
const adSchedulesBulkUpsertModelSelector = state => state.context.data.campaign.data.adSchedulesBulkUpsert;
const finalUrlExpansionsSelector = state => state.context.data.campaign.data.finalUrlExpansions;
const finalUrlExpansionOptOutSelector = state => state.context.data.campaign.data.finalUrlExpansionOptOut;
const finalUrlExpansionsBulkUpsertModelSelector = state => state.context.data.campaign.data.finalUrlExpansionsBulkUpsert;
const bundleCountriesSelector = state => state.context.data.bundleCountries;
const bundleLocationCriterionsSelector = state => state.context.data.bundleLocationCriterions;
const serverErrorsSelector = state => state.context.data.serverErrors;
const systemErrorsSelector = state => state.context.data.systemErrors;
const invalidSaveAttemptSelector = state => state.context.invalidSaveAttempt;
const urlCustomParametersSelector = state => state.context.data.campaign.data.UrlCustomParameters;
const trackingUrlTemplateSelector = state => state.context.data.campaign.data.TrackingUrlTemplate;
const finalUrlSuffixSelector = state => state.context.data.campaign.data.FinalUrlSuffix;
const conversionGoalsSelector = state => state.context.data.campaign.data.conversionGoals;
const permissionsSelector = state => state.context.stepsData.permissions;
const currentLocaleSelector = state => state.context.stepsData.serverContext.CurrentLocale;
const advertiserCustomerIdSelector = state => state.context.stepsData.serverContext.CurrentAccount.AdvertiserCustomerID;
const conversionGoalsDetailsSelector = state => state.context.data.conversionGoalDetails;
const autoGeneratedTextOptOutSelector = state => state.context.data.campaign.data.autoGeneratedTextOptOut;
const autoGeneratedImageOptOutSelector = state => state.context.data.campaign.data.autoGeneratedImageOptOut;
const savedAddedBrandListIdsSelector = state => state.context.data.brandExclusions.data.savedAddedBrandListIds;
const savedDeletedBrandListIdsSelector = state => state.context.data.brandExclusions.data.savedDeletedBrandListIds;
const costPerSaleOptOutSelector = state => state.context.data.campaign.data.costPerSaleOptOut;
const currencySelector = state => state.context.stepsData.serverContext.CurrentAccount.CurrencyCode;
const newCustomerAcquisitionSelector = state => state.context.data.campaign.data.newCustomerAcquisition;
const newCustomerAcquisitionOptOutSelector = state => state.context.data.campaign.data.newCustomerAcquisitionOptOut;

export const useStepCampaignSettingsState = (stepService, activity) => {
  const isMounted = useIsMounted();
  const i18n = useContext(I18nContext);
  const legacyI18n = useMemo(() => buildLegacyI18n(i18n, [ValidationStringMap]), [i18n]);

  const hideStepHeader = useSelector(stepService, hideStepHeaderSelector);
  const scenarioName = useSelector(stepService, scenarioNameSelector);

  const campaignObjective = useSelector(stepService, campaignObjectiveSelector);
  const serverContext = useSelector(stepService, serverContextSelector);
  const campaignId = useSelector(stepService, campaignIdSelector);
  const campaign = useSelector(stepService, campaignSelector);
  const locationTargets = useSelector(stepService, locationTargetsSelector);
  const adSchedules = useSelector(stepService, adSchedulesSelector);
  const adSchedulesBulkUpsert = useSelector(stepService, adSchedulesBulkUpsertModelSelector);
  const finalUrlExpansions = useSelector(stepService, finalUrlExpansionsSelector);
  const finalUrlExpansionOptOut = useSelector(stepService, finalUrlExpansionOptOutSelector);
  const finalUrlExpansionsBulkUpsert = useSelector(stepService, finalUrlExpansionsBulkUpsertModelSelector);
  const autoGeneratedTextOptOut = useSelector(stepService, autoGeneratedTextOptOutSelector);
  const autoGeneratedImageOptOut = useSelector(stepService, autoGeneratedImageOptOutSelector);
  const bundleCountries = useSelector(stepService, bundleCountriesSelector);
  const bundleLocationCriterions = useSelector(stepService, bundleLocationCriterionsSelector);
  const urlCustomParameters = useSelector(stepService, urlCustomParametersSelector);
  const trackingUrlTemplate = useSelector(stepService, trackingUrlTemplateSelector);
  const finalUrlSuffix = useSelector(stepService, finalUrlSuffixSelector);
  const conversionGoalsState = useSelector(stepService, conversionGoalsSelector);
  const conversionGoalsDetails = useSelector(stepService, conversionGoalsDetailsSelector);
  const savedAddedBrandListIds = useSelector(stepService, savedAddedBrandListIdsSelector);
  const savedDeletedBrandListIds = useSelector(stepService, savedDeletedBrandListIdsSelector);
  const costPerSaleOptOut = useSelector(stepService, costPerSaleOptOutSelector);
  const newCustomerAcquisition = useSelector(stepService, newCustomerAcquisitionSelector);
  const newCustomerAcquisitionOptOut = useSelector(stepService, newCustomerAcquisitionOptOutSelector);

  const doesNeedSaving = useSelector(stepService, doesNeedSavingSelector);
  const serverErrors = useSelector(stepService, serverErrorsSelector);
  const systemErrors = useSelector(stepService, systemErrorsSelector);
  const invalidSaveAttempt = useSelector(stepService, invalidSaveAttemptSelector);
  const locale = useSelector(stepService, currentLocaleSelector);
  const permissions = useSelector(stepService, permissionsSelector);
  const advertiserCustomerId = useSelector(stepService, advertiserCustomerIdSelector);
  const currency = useSelector(stepService, currencySelector);

  const [navigationConfirmationPrompt] = useExitWithoutSavingConfirmation(doesNeedSaving, stepService, i18n);

  const conversionGoals = useMemo(() => ({
    conversionGoals: conversionGoalsState,
  }), [conversionGoalsState]);
  const conversionGoalsTitleDetails = useMemo(() => getConversionGoalTitleDetails(i18n, conversionGoalsState), [i18n, conversionGoalsState]);

  const urlOptions = useMemo(() => ({
    TrackingUrlTemplate: trackingUrlTemplate,
    FinalUrlSuffix: finalUrlSuffix,
    UrlCustomParameters: urlCustomParameters,
  }), [trackingUrlTemplate, finalUrlSuffix, urlCustomParameters]);
  const urlOptionsDetails = useMemo(() => getUrlOptionsDetails(i18n, urlOptions), [urlOptions, i18n]);

  const biddingScheme = useMemo(() => ({ BiddingScheme: campaign.data.BiddingScheme }), [campaign.data.BiddingScheme]);
  const bidBudgetTitleDetails = useMemo(
    () => getBidBudgetTitleDetails(serverContext, i18n, campaign.data.BiddingScheme, campaign.data.Budget),
    [campaign.data.BiddingScheme, campaign.data.Budget, i18n, serverContext]
  );

  const storeContainerData = useMemo(() => ({
    ProviderId: campaign.data.ProviderId,
    IsFeedFiltered: campaign.data.IsFeedFiltered,
    SalesCountry: campaign.data.SalesCountry,
    FeedLabel: campaign.data.FeedLabel,
    LocalInventoryAdsEnabled: campaign.data.LocalInventoryAdsEnabled,
  }), [campaign.data.ProviderId, campaign.data.IsFeedFiltered, campaign.data.SalesCountry, campaign.data.FeedLabel, campaign.data.LocalInventoryAdsEnabled]);

  const [storesData, setStoresData] = useState({ error: undefined, stores: {}, countries: {} });
  const onStoreDataLoaded = useCallback(({ error, stores = {}, countries = {} }) => {
    if (isMounted.current) {
      setStoresData({ error, stores, countries });
    }
  }, [isMounted]);

  const storeTitleDetails = useMemo(() => getStoreTitleDetails(i18n, storesData, storeContainerData), [i18n, storesData, storeContainerData]);

  const onCampaignValidation = useCallback((results) => {
    if (!isMounted.current) return;
    const { data: newCampaignData, ...restResults } = results;
    const dataPatch = {
      campaign: {
        ...campaign,
        data: {
          ...campaign.data,
          ...newCampaignData,
        },
        ...restResults,
      },
      activity,
    };
    // log('UPDATE_VALIDATION, results=_, dataPatch=_', results, dataPatch);
    stepService.send({ type: StepEvents.UPDATE_VALIDATION, data: dataPatch });
  }, [stepService, campaign, isMounted, activity]);

  // This callback tracks user-made changes to the page feeds selection.
  const onPageFeedsChange = useCallback((selectedPageFeeds) => {
    if (!isMounted.current) return;
    const dataPatch = {
      campaign: {
        ...campaign,
        isDirty: true,
        data: {
          ...campaign.data,
          pageFeeds: selectedPageFeeds,
        },
      },
    };
    stepService.send({ type: StepEvents.UPDATE_VALIDATION, data: dataPatch });
  }, [stepService, campaign, isMounted]);

  const [reportedSelectedPageFeeds, setReportedSelectedPageFeeds] = useState([]);
  // This callback tracks the page feeds selection reported by the page feed selector
  // and uses the reported information to update the page feeds details.
  const onSelectedPageFeedsUpdate = useCallback((selectedPageFeeds) => {
    if (!isMounted.current) return;
    setReportedSelectedPageFeeds(selectedPageFeeds);
  }, [isMounted]);
  const pageFeedsDetails = useMemo(
    () => {
      if (isEmpty(reportedSelectedPageFeeds)) {
        return i18n.getString(_TL_('Upload all the relevant URLs for your campaign by spreadsheet.'));
      }
      if (reportedSelectedPageFeeds.length === 1) {
        return i18n.getString(_TL_('You’ve successfully added 1 page feed.'));
      }
      return i18n.getString(_TL_('You’ve successfully added {{number}} page feeds.'), { number: reportedSelectedPageFeeds.length });
    },
    [i18n, reportedSelectedPageFeeds]
  );

  // This callback tracks user-made changes to the brand lists selection.
  const onBrandListsChange = useCallback((selectedBrandLists, [deletedBrandListIds, addedBrandListIds]) => {
    if (!isMounted.current) return;
    const dataPatch = {
      brandExclusions: {
        isDirty: true,
        data: {
          deletedBrandListIds,
          addedBrandListIds,
        },
      },
    };
    stepService.send({ type: StepEvents.UPDATE_VALIDATION, data: dataPatch });
  }, [stepService, isMounted]);

  const [reportedSelectedBrandLists, setReportedSelectedBrandLists] = useState([]);
  // This callback tracks the brand lists selection reported by the brand list selector
  // and uses the reported information to update the brand lists panel details.
  const onSelectedBrandListsUpdate = useCallback((selectedBrandLists) => {
    if (!isMounted.current) return;
    setReportedSelectedBrandLists(selectedBrandLists);
  }, [isMounted]);
  const brandListsDetails = useMemo(
    () => {
      if (isEmpty(reportedSelectedBrandLists)) {
        return i18n.getString(_TL_('Add brand exclusions so that your ads won’t show on search queries that mention those brands.'));
      }
      if (reportedSelectedBrandLists.length === 1) {
        return i18n.getString(_TL_('You’ve added 1 brand list, which makes it so that your ads won’t show on search queries that mention those brands.'));
      }
      return i18n.getString(_TL_('You’ve added {{number}} brand lists, which makes it so that your ads won’t show on search queries that mention those brands.'), { number: reportedSelectedBrandLists.length });
    },
    [i18n, reportedSelectedBrandLists]
  );

  const autoGenAssetsSummary = useMemo(() => {
    if (finalUrlExpansionOptOut && autoGeneratedTextOptOut && autoGeneratedImageOptOut) {
      return i18n.getString(_TL_('Off: Do not use autogenerated assets in ads.'));
    }
    return i18n.getString(_TL_('On: You’ve enabled autogenerated assets for your ads.'));
  }, [i18n, finalUrlExpansionOptOut, autoGeneratedTextOptOut, autoGeneratedImageOptOut]);
  const autoGenAssetsData = {
    finalUrlExpansions,
    options: {
      finalUrlExpansionOptOut,
      autoGeneratedTextOptOut,
      autoGeneratedImageOptOut,
    },
  };

  // eslint-disable-next-line no-bitwise
  const [, hasInvalidSaveAttemptChanged] = usePrevProp(invalidSaveAttempt | 0);
  const [, hasServerErrorsChanged] = usePrevProp(serverErrors, serverErrorsCompare);
  const areBeaconsActive = useImpulse(hasInvalidSaveAttemptChanged || hasServerErrorsChanged);

  const [showAllValidationErrors, setShowAllValidationErrors] = useState(false);
  if (hasInvalidSaveAttemptChanged) {
    setShowAllValidationErrors(true);
  }

  const state = {
    isMounted,
    scenarioName,
    campaign,
    biddingScheme,
    bidBudgetTitleDetails,
    adSchedules,
    adSchedulesBulkUpsert,
    finalUrlExpansions,
    finalUrlExpansionsBulkUpsert,
    finalUrlExpansionOptOut,
    locationTargets,
    conversionGoals,
    conversionGoalsTitleDetails,
    conversionGoalsDetails,
    bundleCountries,
    bundleLocationCriterions,
    i18n,
    legacyI18n,
    hideStepHeader,
    campaignId,
    urlOptions,
    urlOptionsDetails,
    showAllValidationErrors,
    areBeaconsActive,
    serverErrors,
    systemErrors,
    navigationConfirmationPrompt,
    campaignObjective,
    storeContainerData,
    storeTitleDetails,
    permissions,
    locale,
    advertiserCustomerId,
    pageFeedsDetails,
    autoGenAssetsData,
    autoGenAssetsSummary,
    brandListsDetails,
    savedAddedBrandListIds,
    savedDeletedBrandListIds,
    costPerSaleOptOut,
    currency,
    newCustomerAcquisition,
    newCustomerAcquisitionOptOut,
  };
  const api = {
    onCampaignValidation, onStoreDataLoaded, onPageFeedsChange, onSelectedPageFeedsUpdate, onBrandListsChange, onSelectedBrandListsUpdate,
  };

  return [state, api];
};
